/**
 * Route /share-receive — atterrissage post-Share Target.
 *
 * Le service worker a intercepté un POST multipart depuis une autre app
 * Android, stocké le contenu dans le SHARE_CACHE, et 303-redirigé vers
 * cette URL en GET. On lit /share-meta (servi par le SW) pour afficher
 * ce que l'utilisateur a partagé et lui offrir des actions :
 *
 *   - Texte / URL : copier dans le presse-papier, ou pousser vers un
 *     host (futur : sélecteur de hôte connecté + clipboard:set)
 *   - Fichier .evtx / .log / .reg : prévisualiser + futurs : upload via
 *     plugin versions
 *
 * Pour l'instant : affichage + bouton "Copier" basique + bouton "Ouvrir
 * la console" qui ferme l'écran et retourne à /console.
 */
import { createResource, For, Show, type Component } from "solid-js";
import { useNavigate } from "@solidjs/router";

interface ShareFile {
  name: string;
  size: number;
  type: string;
  url: string;
}
interface ShareMeta {
  receivedAt: number;
  title: string;
  text: string;
  url: string;
  files: ShareFile[];
}

async function loadMeta(): Promise<ShareMeta | null> {
  try {
    const r = await fetch("/share-meta", { credentials: "same-origin" });
    if (!r.ok) return null;
    return (await r.json()) as ShareMeta;
  } catch {
    return null;
  }
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KiB`;
  return `${(b / 1024 / 1024).toFixed(1)} MiB`;
}

const ShareReceivePage: Component = () => {
  const navigate = useNavigate();
  const [meta] = createResource(loadMeta);

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* clipboard refusé — silencieux */
    }
  }

  async function downloadFile(f: ShareFile) {
    try {
      const r = await fetch(f.url, { credentials: "same-origin" });
      if (!r.ok) return;
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = f.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch {
      /* silencieux */
    }
  }

  return (
    <div class="share-page">
      <header class="share-page__head">
        <h1>Contenu partagé reçu</h1>
        <button class="btn" type="button" onClick={() => navigate("/console/session")}>
          Aller au cockpit
        </button>
      </header>

      <Show when={!meta.loading} fallback={<div class="share-page__loading">Chargement du contenu partagé…</div>}>
        <Show
          when={meta()}
          fallback={
            <div class="share-page__empty">
              Aucun contenu partagé. Cette page n'est utile que lorsque RemoteWS est choisi comme cible de partage
              depuis une autre application.
            </div>
          }
        >
          {(m) => (
            <>
              <p class="share-page__received">
                Reçu le <strong>{new Date(m().receivedAt).toLocaleString()}</strong>
              </p>

              <Show when={m().title}>
                <section class="share-page__section">
                  <div class="share-page__label">Titre</div>
                  <div class="share-page__value">{m().title}</div>
                </section>
              </Show>

              <Show when={m().url}>
                <section class="share-page__section">
                  <div class="share-page__label">URL</div>
                  <div class="share-page__value">
                    <a href={m().url} target="_blank" rel="noopener noreferrer">
                      {m().url}
                    </a>
                    <button class="btn btn--xs btn--ghost" type="button" onClick={() => copyText(m().url)}>
                      Copier
                    </button>
                  </div>
                </section>
              </Show>

              <Show when={m().text}>
                <section class="share-page__section">
                  <div class="share-page__label">Texte</div>
                  <pre class="share-page__text">{m().text}</pre>
                  <button class="btn btn--xs btn--ghost" type="button" onClick={() => copyText(m().text)}>
                    Copier le texte
                  </button>
                </section>
              </Show>

              <Show when={m().files.length > 0}>
                <section class="share-page__section">
                  <div class="share-page__label">Fichiers ({m().files.length})</div>
                  <ul class="share-page__files">
                    <For each={m().files}>
                      {(f) => (
                        <li class="share-page__file">
                          <div class="share-page__file-info">
                            <div class="share-page__file-name">{f.name}</div>
                            <div class="share-page__file-meta">
                              {f.type || "?"} · {formatBytes(f.size)}
                            </div>
                          </div>
                          <button class="btn btn--xs" type="button" onClick={() => downloadFile(f)}>
                            Télécharger
                          </button>
                        </li>
                      )}
                    </For>
                  </ul>
                  <p class="share-page__hint">
                    Pousser ces fichiers vers un host nécessitera le plugin <code>versions</code> — fonctionnalité à
                    venir.
                  </p>
                </section>
              </Show>
            </>
          )}
        </Show>
      </Show>
    </div>
  );
};

export default ShareReceivePage;
